import type { EnrichCreditCategory, EnrichmentCreditsSummary } from '@parcel/types';
import { Progress } from '@parcel/ui';
import { UserSearch } from 'lucide-react';
import {
  formatSpecCreditsFromMicros,
  formatSpecCreditsRemaining,
} from '../../lib/spec-usage-guard.js';
import { SettingsPanel } from './settings-shell.js';

export const ENRICH_CATEGORY_LABELS: Record<EnrichCreditCategory, string> = {
  people_search: 'People found',
  company_search: 'Companies found',
  contact_email: 'Emails',
  contact_profile: 'Full profiles',
  company_profile: 'Company profiles',
  contact_phone: 'Phones',
};

const RESET_DATE = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

/** The plan's enrichment credits this period, and what purchased credits paid past them. */
export function EnrichmentCreditsPanel({ summary }: { summary: EnrichmentCreditsSummary }) {
  const share =
    summary.includedMicros > 0 ? (summary.usedMicros / summary.includedMicros) * 100 : 0;
  const spent = summary.payAsYouGo.filter((row) => row.usedMicros > 0);
  return (
    <SettingsPanel className="p-4" data-testid="enrichment-credits-card">
      <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
        <UserSearch className="size-4 shrink-0 text-brand" aria-hidden />
        Enrichment credits
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">
        {formatSpecCreditsFromMicros(summary.usedMicros)}
        <span className="text-base font-normal text-muted-foreground">
          {' '}
          of {formatSpecCreditsRemaining(summary.includedMicros)} used
        </span>
      </p>
      <Progress value={Math.min(100, share)} aria-label="Enrichment credits" className="mt-3" />
      <p className="mt-2 text-xs text-muted-foreground">
        Resets {RESET_DATE.format(new Date(summary.periodEnd))}. Past them, people searches and
        reveals spend your credit balance.
      </p>
      {spent.length > 0 ? (
        <div className="mt-3 border-t pt-3" data-testid="enrichment-payg">
          <p className="text-xs font-medium text-muted-foreground">Pay-as-you-go this period</p>
          <ul className="mt-1 space-y-1 text-sm">
            {spent.map((row) => (
              <li key={row.category} className="flex justify-between gap-4">
                <span>{ENRICH_CATEGORY_LABELS[row.category]}</span>
                <span className="tabular-nums">
                  {formatSpecCreditsFromMicros(row.usedMicros)} credits
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </SettingsPanel>
  );
}
